import {
  ScopeChangeRequest,
  type ScopeChangeRequestItem,
  ScopeChangeRequestPriceBreakDown,
} from '@/features/admin/scope-change-requests/ScopeChangeRequest'
import type { GetCurrentMemberFn } from '@/features/iam'
import { type ScopeChangeRequestFinalizedDialogViewModel } from '@/features/admin/scope-change-requests/ScopeChangeRequestFinalizedDialog/ScopeChangeRequestFinalizedDialogViewModel'
import { type ScopeChangeRequestRejectDialogViewModel } from '@/features/admin/scope-change-requests/ScopeChangeRequestRejectDialog/ScopeChangeRequestRejectDialogViewModel'
import { type ScopeChangeRequestResolveDialogViewModel } from '@/features/admin/scope-change-requests/ScopeChangeRequestResolveDialog/ScopeChangeRequestResolveDialogViewModel'
import { type ScopeChangeRequestStore } from '@/features/admin/scope-change-requests/ScopeChangeRequestStore'
import { action, computed, observable, runInAction } from 'mobx'
import { CostPerCustomServiceViewModel } from './CostPerCustomServiceViewModel'
import { task } from 'mobx-task'
import { ScopeChangeEditCustomItemsDialogViewModel } from '@/features/admin/scope-change-requests/ScopeChangeEditCustomItemsDialog/ScopeChangeEditCustomItemsDialogViewModel'
import { ScopeChangeEditInventoryItemsDialogViewModel } from '@/features/admin/scope-change-requests/ScopeChangeEditInventoryItemsDialog/ScopeChangeEditInventoryItemsDialogViewModel'
import { type ListInventoryItemsFn } from '@/features/inventory'
import type {
  AlertErrorHandlerFactory,
  AlertFactory,
  AlertRetryWrapperFactory,
} from '@/features/shell'
import { formatMoney } from '@/utils/money-util'
import { ScopeChangeRequestActionSelectionDialogViewModel } from '@/features/admin/scope-change-requests/ScopeChangeRequestActionSelectionDialog/ScopeChangeRequestActionSelectionDialogViewModel'
import type { ActivationLifecycle } from '@/infra/activation'
import { LifecycleTracker } from '@/infra/lifecycle-tracker'
import { ModalViewModel } from '@/infra/ModalViewModel'
import { ViewModelList } from '@/infra/view-model-list'

/**
 * Dependencies of the scope change request details panel.
 */
export interface ScopeChangeRequestDetailsViewModelOptions {
  searchInventoryItems: ListInventoryItemsFn
  scopeChangeRequestStore: ScopeChangeRequestStore
  finalizedDialogVm: ScopeChangeRequestFinalizedDialogViewModel
  resolveDialogVm: ScopeChangeRequestResolveDialogViewModel
  rejectDialogVm: ScopeChangeRequestRejectDialogViewModel
  getCurrentMember: GetCurrentMemberFn
  refreshStatusFilter: () => Promise<void>
  closeNav: () => void
  newAlert: AlertFactory
  createAlertErrorHandler: AlertErrorHandlerFactory
  createAlertRetryWrapper: AlertRetryWrapperFactory
}

export class ScopeChangeRequestDetailsViewModel implements ActivationLifecycle {
  private readonly _lifecycle = new LifecycleTracker()

  @observable.ref private accessor _customItems: ScopeChangeRequestItem[] = []

  private readonly _costPerCustomServiceVms = new ViewModelList({
    models: () => this._customItems,
    create: (item) =>
      new CostPerCustomServiceViewModel(item, this.updatePriceBreakdown),
    activate: (vm) => vm.activate(),
    deactivate: (vm) => vm.deactivate(),
  })

  readonly modal = new ModalViewModel({
    onClose: () => this.setScopeChangeRequest(ScopeChangeRequest.none),
  })

  @observable accessor scopeChangeRequest = ScopeChangeRequest.none
  @observable accessor percentageChangeApplied = false
  @observable accessor isBundleChanging = false
  @observable accessor scopeChangeRequestPriceBreakDown =
    ScopeChangeRequestPriceBreakDown.none
  @observable accessor customItemsExpanded = false
  @observable accessor serviceItemsExpanded = false
  @observable accessor bundlesExpanded = false

  readonly scopeChangeEditCustomItemsDialogVm

  readonly scopeChangeEditInventoryItemsDialogVm

  readonly scopeChangeRequestActionSelectionDialogVm

  constructor(
    private readonly _options: ScopeChangeRequestDetailsViewModelOptions,
  ) {
    const {
      searchInventoryItems,
      scopeChangeRequestStore,
      refreshStatusFilter,
      newAlert,
      createAlertErrorHandler,
    } = _options

    this.scopeChangeEditCustomItemsDialogVm =
      new ScopeChangeEditCustomItemsDialogViewModel(
        scopeChangeRequestStore,
        this.handleItemsEdited,
        newAlert,
        createAlertErrorHandler,
      )

    this.scopeChangeEditInventoryItemsDialogVm =
      new ScopeChangeEditInventoryItemsDialogViewModel(
        searchInventoryItems,
        scopeChangeRequestStore,
        this.handleItemsEdited,
        newAlert,
        createAlertErrorHandler,
        _options.createAlertRetryWrapper,
      )

    this.scopeChangeRequestActionSelectionDialogVm =
      new ScopeChangeRequestActionSelectionDialogViewModel(
        scopeChangeRequestStore,
        refreshStatusFilter,
        this.handleItemsEdited,
        createAlertErrorHandler,
      )

    this._lifecycle.manage(this.scopeChangeEditInventoryItemsDialogVm)
    this._lifecycle.manage(this._costPerCustomServiceVms)
  }

  /**
   * The editable prices of the request's custom items.
   */
  @computed
  get costPerCustomServiceItems() {
    return this._costPerCustomServiceVms.items
  }

  /**
   * Reloads the price breakdown after the request's items were edited.
   */
  @action.bound
  async handleItemsEdited() {
    await this._reloadPriceBreakDown()
    this.setCostPerCustomServiceItems(this.scopeChangeRequest.customItems)
  }

  /**
   * Saves the edited custom item prices and reloads the price breakdown.
   */
  @action.bound
  async updatePriceBreakdown() {
    if (
      !this.canEditItems ||
      this.scopeChangeRequest === ScopeChangeRequest.none
    ) {
      return
    }

    // Prices save as they are typed; the alerts have reported a failure.
    await this._savePrices().catch(() => undefined)
  }

  /**
   * Whether the request's items can still be edited.
   */
  @computed
  get canEditItems() {
    return (
      this.scopeChangeRequest.isPendingFinalization ||
      this.scopeChangeRequest.isPendingAdminApproval
    )
  }

  /**
   * The custom item rows: the applied changes once accepted, the editable
   * prices otherwise.
   */
  @computed
  get customItemRows() {
    return this.scopeChangeRequest.isAccepted
      ? this.scopeChangeRequestPriceBreakDown.customScopeChangeItems
      : this.costPerCustomServiceItems
  }

  @computed
  get jobBundle() {
    return this.scopeChangeRequest.job.jobBundle
  }

  @computed
  get bundleInfo() {
    if (!this.jobBundle) {
      return
    }

    if (this.isBundleChanging) {
      const oldBundleName =
        this.scopeChangeRequestPriceBreakDown.bundleScopeChangeItems[0]
          .displayName
      const newBundleName =
        this.scopeChangeRequestPriceBreakDown.bundleScopeChangeItems[1]
          .displayName

      return `"${oldBundleName}" is changing to "${newBundleName}"`
    }

    return this.jobBundle.name
  }

  @computed
  get percentageNote() {
    const totalPriceDiff =
      this.scopeChangeRequestPriceBreakDown.percentageInventoryScopeChangeItems.reduce(
        (prev, i) => prev + i.priceDiff,
        0,
      )
    const totalProPriceDiff =
      this.scopeChangeRequestPriceBreakDown.percentageInventoryScopeChangeItems.reduce(
        (prev, i) => prev + i.proPriceDiff,
        0,
      )

    return `Includes a ${formatMoney(
      totalPriceDiff,
      true,
    )} price difference and a ${formatMoney(
      totalProPriceDiff,
      true,
    )} pro cut difference due to percentage items`
  }

  /**
   * Whether or not the current user has permission to resolve job scope changes
   */
  @computed
  get canResolveScopeChangeRequest() {
    return this._options.getCurrentMember(this.scopeChangeRequest.workspace.id)
      .permissions.adminJobScopeChangeResolve
  }

  /**
   * Checks if the job for the scope change request selected is closed.
   */
  @computed
  get isJobClosed() {
    return this.scopeChangeRequest.job.status === 'Closed'
  }

  @action.bound
  toggleCustomItemsSection() {
    this.customItemsExpanded = !this.customItemsExpanded
  }

  @action.bound
  toggleServiceItemsSection() {
    this.serviceItemsExpanded = !this.serviceItemsExpanded
  }

  @action.bound
  toggleBundlesSection() {
    this.bundlesExpanded = !this.bundlesExpanded
  }

  /**
   * Resets the editable prices to the given custom items.
   * @param items
   */
  @action.bound
  setCostPerCustomServiceItems(items: ScopeChangeRequestItem[]) {
    // Items that stay keep their view model, so their prices are reset here.
    for (const vm of this._costPerCustomServiceVms.items) {
      vm.reset()
    }
    this._customItems = items

    this.customItemsExpanded = items.length > 0
  }

  /**
   * Sets the scope change request displayed in the panel
   * @param changeRequest
   */
  @action.bound
  setScopeChangeRequest(changeRequest: ScopeChangeRequest) {
    this.scopeChangeRequest = changeRequest
    this.setCostPerCustomServiceItems(this.scopeChangeRequest.customItems)
  }

  /**
   * Opens the panel, then loads the request's price breakdown.
   * @param changeRequest
   */
  @action.bound
  show(changeRequest: ScopeChangeRequest) {
    this.setScopeChangeRequest(changeRequest)
    this.modal.show()
    this._options.closeNav()
    void this._reloadPriceBreakDown()
  }

  /**
   * Opens the dialog for editing the request's custom items.
   */
  @action.bound
  editCustomItems() {
    this.scopeChangeEditCustomItemsDialogVm.open(this.scopeChangeRequest)
  }

  /**
   * Opens the dialog for editing the request's service items.
   */
  @action.bound
  editInventoryItems() {
    this.scopeChangeEditInventoryItemsDialogVm.open(this.scopeChangeRequest)
  }

  /**
   * Admin approves if scope change is on PendingAdminApproval
   */
  adminApprove = task.resolved(async () => {
    if (!this.scopeChangeRequest.isPendingAdminApproval) {
      return
    }

    if (this.scopeChangeRequest.requireClientApproval) {
      this.scopeChangeRequestActionSelectionDialogVm.open(
        this.scopeChangeRequest,
      )
      return
    }

    await this._options.scopeChangeRequestStore
      .approveScopeChangeRequest(this.scopeChangeRequest)
      .catch(
        this._options.createAlertErrorHandler(
          'Approving the change request failed',
        ),
      )
    await this._options.refreshStatusFilter()

    this.setCostPerCustomServiceItems(this.scopeChangeRequest.customItems)
    this._options.finalizedDialogVm.open(this.scopeChangeRequest)
  })

  /**
   * Finalizes the scope change requests and approves it
   */
  finalize = task.resolved(async () => {
    if (!this.scopeChangeRequest.isPendingFinalization) {
      return
    }

    if (!this.costPerCustomServiceItems.every((vm) => vm.validate())) {
      return
    }

    // Every item's debounced save is cancelled, not just the first pending one.
    const hasUnsavedPrices = this.costPerCustomServiceItems
      .map((vm) => vm.cancelPendingUpdate())
      .some(Boolean)
    if (hasUnsavedPrices) {
      await this._savePrices()
    }

    const viewModels = this.scopeChangeRequest.customItems.map((i) => ({
      id: i.id,
      price: i.price,
      proPrice: i.proPrice,
    }))

    await this._options.scopeChangeRequestStore
      .finalizeScopeChangeRequest(this.scopeChangeRequest, viewModels)
      .catch(
        this._options.createAlertErrorHandler(
          'Finalizing the change request failed',
        ),
      )

    await this.adminApprove()
  })

  /**
   * Opens the resolve dialog
   */
  @action.bound
  resolve() {
    this._options.resolveDialogVm.open(this.scopeChangeRequest)
  }

  /**
   * Opens the decline dialog
   */
  @action.bound
  decline() {
    this._options.rejectDialogVm.open(this.scopeChangeRequest)
  }

  /** @inheritDoc */
  activate() {
    this._lifecycle.activate()
  }

  /** @inheritDoc */
  deactivate() {
    this._lifecycle.deactivate()
  }

  private async _savePrices() {
    await this._saveCustomItemPrices().catch(
      this._options.createAlertErrorHandler('Saving the prices failed'),
    )
    await this._reloadPriceBreakDown()
    const withRetry = this._options.createAlertRetryWrapper(
      'Loading the change request failed',
      this._lifecycle.signal,
    )
    await withRetry(() =>
      this._options.scopeChangeRequestStore.fetchScopeChangeRequest(
        this.scopeChangeRequest.id,
        this._lifecycle.signal,
      ),
    )
  }

  private async _saveCustomItemPrices() {
    await this._options.scopeChangeRequestStore.editScopeChangeRequest({
      scopeChangeRequestId: this.scopeChangeRequest.id,
      customItems: this.costPerCustomServiceItems.map((i) => ({
        name: i.fromItem.name,
        quantity: i.fromItem.quantity,
        price: i.newPrice ?? 0,
        proPrice: i.newProPrice ?? 0,
      })),
      inventoryItems: this.scopeChangeRequest.inventoryItems.map((i) => ({
        versionId: i.inventoryItemVersionId,
        quantity: i.quantity,
      })),
    })
  }

  private async _reloadPriceBreakDown() {
    const scopeChangeRequest = this.scopeChangeRequest
    const withRetry = this._options.createAlertRetryWrapper(
      'Loading the price breakdown failed',
      this._lifecycle.signal,
    )
    // The panel keeps the previous breakdown when the alert is dismissed.
    await withRetry(() => this._loadPriceBreakDown(scopeChangeRequest)).catch(
      () => undefined,
    )
  }

  private async _loadPriceBreakDown(scopeChangeRequest: ScopeChangeRequest) {
    const priceBreakDown =
      await this._options.scopeChangeRequestStore.fetchScopeChangeRequestPriceBreakDown(
        scopeChangeRequest,
        this._lifecycle.signal,
      )
    if (scopeChangeRequest !== this.scopeChangeRequest) {
      return
    }

    runInAction(() => {
      this.scopeChangeRequestPriceBreakDown = priceBreakDown
      this.percentageChangeApplied =
        priceBreakDown.percentageInventoryScopeChangeItems.length > 0
      this.isBundleChanging = priceBreakDown.bundleScopeChangeItems.length === 2
      this.serviceItemsExpanded =
        priceBreakDown.flatInventoryScopeChangeItems.length > 0
      this.bundlesExpanded = priceBreakDown.bundleScopeChangeItems.length > 0
    })
  }
}
